import React, { useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  Typography,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  Skeleton,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
} from '@mui/icons-material';
import { getImageUrl, handleImageError } from '../utils/imageUtils';

const AdminTable = ({
  title,
  columns,
  data,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  onSearch,
  onAdd,
  onEdit,
  onDelete,
  onView,
  onSort,
  onFilter,
  categories = [],
  loading = false,
  showPagination = true,
}) => {
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [sortAnchorEl, setSortAnchorEl] = useState(null);

  const handleFilterClick = (event) => setFilterAnchorEl(event.currentTarget);
  const handleFilterClose = () => setFilterAnchorEl(null);

  const handleSortClick = (event) => setSortAnchorEl(event.currentTarget);
  const handleSortClose = () => setSortAnchorEl(null);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1A1A1A' }}>
          {title}
        </Typography>
        {onAdd && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onAdd}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              px: 3,
              py: 1,
              bgcolor: '#F25828',
              '&:hover': { bgcolor: '#d44a20' },
              boxShadow: '0px 4px 12px rgba(242, 88, 40, 0.2)',
            }}
          >
            Add New
          </Button>
        )}
      </Box>

      <Paper sx={{ width: '100%', mb: 2, borderRadius: '16px', overflow: 'hidden', boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)' }}>
        <Box sx={{ p: 2, borderBottom: '1px solid #F0F0F0', display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Search..."
            size="small"
            onChange={(e) => onSearch(e.target.value)}
            sx={{
              flex: 1,
              maxWidth: 400,
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                bgcolor: '#F8F9FA',
                '& fieldset': { border: 'none' },
              },
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#999' }} />
                  </InputAdornment>
                ),
              },
            }}
          />
          
          <Stack direction="row" spacing={1}>
            {/* Sort Menu */}
            <IconButton 
              sx={{ borderRadius: '10px', bgcolor: '#F8F9FA', p: 1.2 }}
              onClick={handleSortClick}
            >
              <SortIcon sx={{ color: '#666' }} />
            </IconButton>

            {/* Filter Menu */}
            <IconButton 
              sx={{ borderRadius: '10px', bgcolor: '#F8F9FA', p: 1.2 }}
              onClick={handleFilterClick}
            >
              <FilterIcon sx={{ color: '#666' }} />
            </IconButton>
          </Stack>
        </Box>

        {/* Sort Popover Menu */}
        <Menu
          anchorEl={sortAnchorEl}
          open={Boolean(sortAnchorEl)}
          onClose={handleSortClose}
          slotProps={{ paper: { sx: { borderRadius: '12px', mt: 1, minWidth: 180 } } }}
        >
          <Typography sx={{ px: 2, py: 1, fontWeight: 600, fontSize: '12px', color: '#999' }}>SORT BY</Typography>
          {columns.filter(c => c.id !== 'image' && c.id !== 'status').map((col) => (
            <MenuItem key={col.id} onClick={() => { onSort(col.id); handleSortClose(); }}>
              {col.label}
            </MenuItem>
          ))}
        </Menu>

        {/* Filter Popover Menu */}
        <Menu
          anchorEl={filterAnchorEl}
          open={Boolean(filterAnchorEl)}
          onClose={handleFilterClose}
          slotProps={{ paper: { sx: { borderRadius: '12px', mt: 1, minWidth: 200 } } }}
        >
          <Typography sx={{ px: 2, py: 1, fontWeight: 600, fontSize: '12px', color: '#999' }}>FILTER BY CATEGORY</Typography>
          <MenuItem onClick={() => { onFilter('category', ''); handleFilterClose(); }}>All Categories</MenuItem>
          {categories.map((cat) => (
            <MenuItem key={cat.id || cat} onClick={() => { onFilter('category', cat.name || cat); handleFilterClose(); }}>
              {cat.name || cat}
            </MenuItem>
          ))}
          
          <Typography sx={{ px: 2, py: 1, mt: 1, fontWeight: 600, fontSize: '12px', color: '#999' }}>FILTER BY DATE</Typography>
          <MenuItem onClick={() => { onFilter('date', 'today'); handleFilterClose(); }}>Today</MenuItem>
          <MenuItem onClick={() => { onFilter('date', 'week'); handleFilterClose(); }}>This Week</MenuItem>
          <MenuItem onClick={() => { onFilter('date', 'month'); handleFilterClose(); }}>This Month</MenuItem>
          <MenuItem onClick={() => { onFilter('date', ''); handleFilterClose(); }}>Clear Date Filter</MenuItem>
        </Menu>

        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
            <TableHead>
              <TableRow sx={{ bgcolor: '#F8F9FA' }}>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align || 'left'}
                    sx={{ fontWeight: 600, color: '#666', py: 2 }}
                  >
                    {column.label}
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ fontWeight: 600, color: '#666', py: 2 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
            {loading ? (
              Array.from(new Array(rowsPerPage)).map((_, rowIndex) => (
                <TableRow key={rowIndex}>
                  {columns.map((column, colIndex) => (
                    <TableCell key={colIndex}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                  <TableCell>
                    <Skeleton variant="circular" width={40} height={40} />
                  </TableCell>
                </TableRow>
              ))
            ) : data.length > 0 ? (
              data.map((row, index) => (
                <TableRow
                  hover
                  tabIndex={-1}
                  key={row.id || index}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  {columns.map((column) => {
                    const value = row[column.id];
                    return (
                      <TableCell key={column.id} align={column.align || 'left'} sx={{ py: 2 }}>
                        {column.render ? column.render(value, row) : value}
                      </TableCell>
                    );
                  })}
                  <TableCell align="right" sx={{ py: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                      {onView && (
                        <IconButton size="small" onClick={() => onView(row)} sx={{ color: '#3C60B6' }}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      )}
                      {onEdit && (
                        <IconButton size="small" onClick={() => onEdit(row)} sx={{ color: '#F25828' }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                      {onDelete && (
                        <IconButton size="small" onClick={() => onDelete(row)} sx={{ color: '#d32f2f' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 10 }}>
                  <Typography color="textSecondary">No records found</Typography>
                </TableCell>
              </TableRow>
            )}
            </TableBody>
          </Table>
        </TableContainer>

        {showPagination && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalCount || 0}
            rowsPerPage={rowsPerPage || 10}
            page={page || 0}
            onPageChange={onPageChange || (() => {})}
            onRowsPerPageChange={onRowsPerPageChange || (() => {})}
            sx={{ borderTop: '1px solid #F0F0F0' }}
          />
        )}
      </Paper>
    </Box>
  );
};

export default AdminTable;
